import React from 'react'

import { Avatar, Box, useClassNames } from '@factorialco/deprecated-design-system'
import { ItManagementWizardSoftwareSetupFragment } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import createComputed from 'modules/core/hooks/createComputed'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { UnionSoftwareType } from 'modules/it_management/lib/software'

import SoftwareList from './_SoftwareList'
import * as styles from './index.vanilla.css'

interface SoftwareSelectorProps {
  softwares: UnionSoftwareType[]
  softwareSetups: ItManagementWizardSoftwareSetupFragment[]
  selectSoftware: (value: string) => void
  selectCustomSoftware: (value: string) => void
  softwareId: string
  softwareName: string
}

const useSearchFilter = createComputed((search: string, softwares: UnionSoftwareType[]) => {
  return softwares.filter((software) => {
    return matchText(search, software.name)
  })
})

const SoftwareSelector = ({
  softwares,
  softwareSetups,
  selectSoftware,
  selectCustomSoftware,
  softwareId,
  softwareName,
}: SoftwareSelectorProps) => {
  const notInstalledSoftwares = softwares.filter((s) => {
    return !softwareSetups.some((ss: ItManagementWizardSoftwareSetupFragment) => {
      return s.id === (ss.software ? ss.software.id : ss.customSoftware?.id?.toString())
    })
  })
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const defaultSoftware = softwares.find((s) => s.id == softwareId)
  const [text, setText] = React.useState<string>(
    defaultSoftware ? defaultSoftware.name : softwareName
  )
  const [open, setOpen] = React.useState<boolean>(false)
  const [software, setSoftware] = React.useState<UnionSoftwareType | undefined>(defaultSoftware)
  const { debouncedSearch, onSearch } = useDebouncedSearch()
  const filteredSoftwares = useSearchFilter(debouncedSearch, notInstalledSoftwares).get()

  useEffect(() => {
    if (filteredSoftwares.length < notInstalledSoftwares.length) {
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSoftwares.length])

  useEffect(() => {
    if (open) {
      // setTimeout adds another callback in the queue, but is called later than popup opens up
      setTimeout(() => {
        inputRef.current!.focus()
      }, 0)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useUnsafeEffect(() => {
    if (software) {
      setText(software!.name)
      selectSoftware(software!.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [software?.id])

  const onSelectSoftware = (id: string) => {
    const selectedSoftware = filteredSoftwares.find((fs) => fs.id === id)
    setSoftware(selectedSoftware)
    setOpen(false)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    onSearch(event)

    const softwareByName = filteredSoftwares.find((fs) => fs.name === value)

    if (softwareByName) {
      setSoftware(softwareByName)
    } else {
      selectCustomSoftware(value)
      setText(value)
      setSoftware(undefined)

      if (value === 'akdeyfursne123i1') {
        throw new Error('Sentry test error reporting')
      }
    }
  }
  const handleClick = () => {
    setOpen(true)
  }
  const cx = useClassNames(styles)

  return (
    <PopoverMenu open={open} type="list">
      <PopoverMenu.Trigger asChild>
        <Box width="full" className={cx('softwareSelector')} onClick={handleClick}>
          {(debouncedSearch || software) && (
            <Avatar size="s24" url={software?.logoUrl} label={debouncedSearch} />
          )}
          <Box width="full" justifyContent="center">
            <input
              width={'full'}
              type="text"
              alt="software searchbox"
              ref={inputRef}
              value={text}
              onClick={handleClick}
              onChange={handleInputChange}
              placeholder={i18n.t(
                'it_management.initial_setup.wizard.software_selector.placeholder'
              )}
            />
          </Box>
        </Box>
      </PopoverMenu.Trigger>

      <PopoverMenu.Content
        side="bottom"
        align="center"
        width={filteredSoftwares.length > 0 ? 's400' : 's0'}
        onInteractOutside={() => {
          setOpen(false)
        }}
      >
        <SoftwareList softwares={filteredSoftwares} onSelect={onSelectSoftware} />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default SoftwareSelector
